import { createFileRoute } from '@tanstack/react-router'
import { Link } from '@tanstack/react-router'
import { Target, Eye, Lightbulb, Users, Zap, Shield, Heart, ArrowRight } from 'lucide-react'
import { Button } from '../components/ui/button'

export const Route = createFileRoute('/about')({
  component: AboutPage,
})

const values = [
  {
    icon: Lightbulb,
    title: "Innovation First",
    description: "We stay ahead of technology trends to deliver cutting-edge solutions that give your business a competitive edge."
  },
  {
    icon: Users,
    title: "Client Partnership",
    description: "We view every project as a partnership. Your success is our success, and we're invested in your long-term growth."
  },
  {
    icon: Zap,
    title: "Quality & Speed",
    description: "We deliver high-quality solutions without compromising on timelines. Efficiency and excellence go hand in hand."
  },
  {
    icon: Shield,
    title: "Transparency",
    description: "Clear communication, honest pricing, and no hidden surprises. We believe trust is the foundation of great work."
  }
]

const milestones = [
  { year: "2023", title: "Founded", description: "LogicNext was born with a vision to deliver world-class digital solutions from Bangladesh." },
  { year: "2023", title: "First Major Project", description: "Delivered our first enterprise software solution for a healthcare provider." },
  { year: "2024", title: "Team Growth", description: "Expanded our team with specialized developers and designers." },
  { year: "2024", title: "AI Integration", description: "Added AI and automation services to our portfolio." },
  { year: "2025", title: "Going Global", description: "Serving clients across Bangladesh and beyond with scalable solutions." }
]

function AboutPage() {
  return (
    <>
      {/* Hero Section */}
      <section className="relative z-10 pt-32 pb-16 container mx-auto px-6">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <p className="text-sm font-medium text-indigo-400">About Us</p>
          <h1 className="text-4xl md:text-6xl font-bold">
            Building the Future, <br />
            <span className="bg-linear-to-r from-indigo-400 to-blue-500 bg-clip-text text-transparent">
              One Line of Code at a Time
            </span>
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            We're a passionate team of developers, designers, and problem-solvers
            dedicated to helping businesses thrive in the digital age.
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="relative z-10 py-16 container mx-auto px-6">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8">
          {/* Mission */}
          <div className="bg-slate-900/30 border border-white/5 rounded-3xl p-8 hover:border-white/10 transition-all duration-300">
            <div className="flex items-center gap-4 mb-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-linear-to-br from-indigo-500 to-purple-500">
                <Target className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold">Our Mission</h2>
            </div>
            <p className="text-slate-300 leading-relaxed">
              To empower businesses in Bangladesh and beyond with innovative, accessible,
              and affordable digital solutions. We bridge the gap between cutting-edge
              technology and practical business needs, ensuring every client—regardless of
              size—can compete in the digital marketplace.
            </p>
          </div>

          {/* Vision */}
          <div className="bg-slate-900/30 border border-white/5 rounded-3xl p-8 hover:border-white/10 transition-all duration-300">
            <div className="flex items-center gap-4 mb-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-linear-to-br from-blue-500 to-cyan-500">
                <Eye className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold">Our Vision</h2>
            </div>
            <p className="text-slate-300 leading-relaxed">
              To become Bangladesh's most trusted technology partner, known for delivering
              world-class digital experiences. We envision a future where every business
              has access to the tools and technology they need to succeed, and we're
              committed to making that vision a reality.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="relative z-10 py-16 container mx-auto px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Core Values</h2>
            <p className="text-slate-400">The principles that guide everything we do</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon
              return (
                <div
                  key={index}
                  className="bg-slate-900/30 border border-white/5 rounded-2xl p-6 hover:border-indigo-500/30 transition-all duration-300 group"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-500/10 mb-4 group-hover:bg-indigo-500/20 transition-colors">
                    <Icon className="h-6 w-6 text-indigo-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{value.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{value.description}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="relative z-10 py-16 container mx-auto px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Journey</h2>
            <p className="text-slate-400">From idea to impact</p>
          </div>

          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={index} className="flex gap-6">
                {/* Year */}
                <div className="shrink-0 w-16">
                  <div className="text-indigo-400 font-bold">{milestone.year}</div>
                </div>

                {/* Line */}
                <div className="flex flex-col items-center">
                  <div className="w-3 h-3 rounded-full bg-indigo-500" />
                  {index < milestones.length - 1 && (
                    <div className="w-0.5 h-full bg-white/10 mt-2" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 pb-8">
                  <h3 className="text-lg font-semibold text-white mb-1">{milestone.title}</h3>
                  <p className="text-slate-400 text-sm">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="relative z-10 py-16 container mx-auto px-6">
        <div className="max-w-5xl mx-auto bg-linear-to-br from-slate-900/50 to-slate-800/30 border border-white/5 rounded-3xl p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Why Choose LogicNext?</h2>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Heart className="h-5 w-5 text-indigo-400 shrink-0 mt-1" />
                  <span className="text-slate-300">Local expertise with global standards</span>
                </li>
                <li className="flex items-start gap-3">
                  <Heart className="h-5 w-5 text-indigo-400 shrink-0 mt-1" />
                  <span className="text-slate-300">Competitive pricing for Bangladesh market</span>
                </li>
                <li className="flex items-start gap-3">
                  <Heart className="h-5 w-5 text-indigo-400 shrink-0 mt-1" />
                  <span className="text-slate-300">Dedicated support in Bangla and English</span>
                </li>
                <li className="flex items-start gap-3">
                  <Heart className="h-5 w-5 text-indigo-400 shrink-0 mt-1" />
                  <span className="text-slate-300">Modern tech stack with proven reliability</span>
                </li>
                <li className="flex items-start gap-3">
                  <Heart className="h-5 w-5 text-indigo-400 shrink-0 mt-1" />
                  <span className="text-slate-300">End-to-end service from concept to launch</span>
                </li>
              </ul>
            </div>
            <div className="text-center md:text-left">
              <div className="inline-block bg-indigo-500/10 border border-indigo-500/20 rounded-2xl p-8">
                <div className="text-5xl font-bold text-white mb-2">98%</div>
                <div className="text-indigo-400 font-medium mb-4">Client Satisfaction</div>
                <p className="text-slate-400 text-sm">
                  Our clients love working with us, and we're proud of the relationships we've built.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-24 container mx-auto px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Let's Build Something Great Together</h2>
          <p className="text-slate-400 mb-8">
            Ready to transform your ideas into reality? We'd love to hear about your project
            and explore how we can help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-indigo-600 hover:bg-indigo-700">
              <Link to="/contact" className="flex items-center gap-2">
                Get In Touch
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/10 hover:bg-white/5">
              <Link to="/solutions">Explore Solutions</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  )
}
